/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: 视频卡片组件 - 支持单视频播放
 */
import Image from 'next/image';
import React, { useCallback, useEffect, useRef } from 'react';

import { useVideoPlayerStore } from '@/landing/store/videoPlayer';

import styles from './VideoCard.module.scss';

interface VideoCardProps {
  /** 视频唯一 ID */
  id: string;
  /** 视频 URL */
  videoUrl: string;
  /** 封面图 URL */
  posterUrl: string;
  /** 视频 alt 文本 */
  alt: string;
}

const VideoCard: React.FC<VideoCardProps> = ({ id, videoUrl, posterUrl, alt }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { currentPlayingId, play, pause } = useVideoPlayerStore();

  const isPlaying = currentPlayingId === id;

  // 监听全局播放状态变化
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.play().catch(() => {
        // 自动播放被阻止时静默处理
      });
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }, [isPlaying]);

  // 视频结束时暂停
  const handleEnded = useCallback(() => {
    pause();
  }, [pause]);

  // 点击切换播放状态
  const handleClick = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play(id);
    }
  }, [id, isPlaying, play, pause]);

  return (
    <div className={styles.videoCard} onClick={handleClick}>
      {/* 封面图 */}
      <div className={`${styles.poster} ${isPlaying ? styles.hidden : ''}`}>
        <Image
          src={posterUrl}
          alt={alt}
          fill
          className={styles.posterImage}
          sizes="200px"
        />
      </div>

      {/* 视频 */}
      <video
        ref={videoRef}
        className={styles.video}
        src={videoUrl}
        poster={posterUrl}
        playsInline
        muted={false}
        onEnded={handleEnded}
      />
    </div>
  );
};

export default VideoCard;
